import { EventEmitter } from 'events';
import { supabase } from '../lib/db';
import { unifiedNotificationService } from './UnifiedNotificationService';
import { getEthiopianDateStr, getTimeStr } from '../utils/ethiopianCalendar';
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

interface NotificationTemplate {
  name: string;
  category?: string;
  metadata?: Record<string, any>;
}

interface DatabaseNotification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  entity_type: string;
  status: string;
  metadata?: Record<string, any>;
  deep_link?: string;
  scheduled_for: string;
  delivered_at?: string[];
  created_at: string;
}

interface Profile {
  contact_name?: string;
  email: string;
}

interface Message {
  id: string;
  content: string;
  sender_id: string;
  conversation_id: string;
  created_at: string;
  notification_status?: string;
  conversations: {
    initiator_id: string;
    recipient_id: string;
  };
  sender: Profile;
}

interface PendingMessage {
  id: string;
  content: string;
  sender_id: string;
  conversation_id: string;
  created_at: string;
  notification_status: string;
  conversations: {
    initiator_id: string;
    recipient_id: string;
  };
  sender: {
    contact_name: string | null;
    email: string;
  };
}

interface MessageWithRelations extends Omit<Message, 'conversations' | 'sender'> {
  conversations: {
    initiator_id: string;
    recipient_id: string;
  };
  sender: {
    contact_name?: string;
    email: string;
  };
}

class NotificationService extends EventEmitter {
  private static instance: NotificationService;
  private pollingInterval: NodeJS.Timeout | null = null;
  private scheduledNotifications: Map<string, NodeJS.Timeout> = new Map();
  private isInitialized: boolean = false;
  private messageChannel: RealtimeChannel | null = null;
  private notificationChannel: RealtimeChannel | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private maxReconnectAttempts = 5;
  private reconnectAttempts = 0;
  private userId: string | null = null;
  private lastChannelState: 'SUBSCRIBED' | 'CLOSED' | 'TIMED_OUT' | 'CHANNEL_ERROR' = 'CLOSED';
  private lastPollTime: number = 0;
  private readonly POLL_INTERVAL = 60 * 1000; // 1 minute in milliseconds
  private readonly MIN_POLL_INTERVAL = 30 * 1000; // 30 seconds minimum between polls

  private constructor() {
    super();
    console.log('[Notifications] Initializing NotificationService singleton');
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  private async setupSubscriptions(userId: string) {
    console.log('[Notifications] Setting up subscriptions for user:', userId);
    
    try {
      // Clean up existing subscriptions if any
      await this.cleanupSubscriptions();

      // Create new message channel — listen to all new messages, then filter in the callback
      // (messages table has no recipient_id column; recipient is determined via conversations)
      this.messageChannel = supabase
        .channel(`messages-${userId}`)
        .on(
          'postgres_changes' as any,
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
          },
          async (payload: RealtimePostgresChangesPayload<any>) => {
            console.log('[Notifications] Received message event:', payload);
            if (payload.new && payload.eventType === 'INSERT') {
              // Skip messages sent by the current user
              if (payload.new.sender_id === userId) return;
              // Fetch full message with relations for notification
              try {
                const { data: msg, error } = await supabase
                  .from('messages')
                  .select(`
                    id, content, sender_id, conversation_id, created_at, notification_status,
                    conversations!inner (initiator_id, recipient_id),
                    sender:profiles!sender_id (contact_name, email)
                  `)
                  .eq('id', payload.new.id)
                  .single();
                if (error || !msg) return;
                // Only notify if the current user is a participant in this conversation
                const conv = msg.conversations as any;
                if (conv.initiator_id !== userId && conv.recipient_id !== userId) return;
                await this.handleNewMessage({
                  ...msg,
                  conversations: { initiator_id: conv.initiator_id, recipient_id: conv.recipient_id },
                  sender: { contact_name: (msg.sender as any)?.contact_name, email: (msg.sender as any)?.email }
                } as Message);
              } catch (e) {
                console.error('[Notifications] Error processing realtime message:', e);
              }
            }
          }
        );

      // Create new notification channel
      this.notificationChannel = supabase
        .channel(`notifications-${userId}`)
        .on(
          'postgres_changes' as any,
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`,
          },
          async (payload: RealtimePostgresChangesPayload<{ new: DatabaseNotification }>) => {
            console.log('[Notifications] Received notification event:', payload);
            if (payload.new && 'status' in payload.new && payload.new.status === 'pending') {
              await this.processScheduledNotification(payload.new as DatabaseNotification);
            }
          }
        );

      // Subscribe to channels with status monitoring and automatic reconnection
      await Promise.all([
        this.messageChannel.subscribe(async (status) => {
          console.log('[Notifications] Message channel status:', status);
          this.lastChannelState = status;
          
          if (status === 'SUBSCRIBED') {
            this.reconnectAttempts = 0;
          } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            await this.handleChannelError('message');
          }
        }),
        this.notificationChannel.subscribe(async (status) => {
          console.log('[Notifications] Notification channel status:', status);
          this.lastChannelState = status;
          
          if (status === 'SUBSCRIBED') {
            this.reconnectAttempts = 0;
          } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            await this.handleChannelError('notification');
          }
        })
      ]);

      console.log('[Notifications] Subscriptions setup completed');
    } catch (error) {
      console.error('[Notifications] Error setting up subscriptions:', error);
      await this.handleChannelError('both');
    }
  }

  private async cleanupSubscriptions() {
    if (this.messageChannel) {
      try {
        await this.messageChannel.unsubscribe();
      } catch (error) {
        console.error('[Notifications] Error unsubscribing from message channel:', error);
      }
      this.messageChannel = null;
    }
    
    if (this.notificationChannel) {
      try {
        await this.notificationChannel.unsubscribe();
      } catch (error) {
        console.error('[Notifications] Error unsubscribing from notification channel:', error);
      }
      this.notificationChannel = null;
    }
  }

  private isStopping = false;

  private async handleChannelError(channelType: 'message' | 'notification' | 'both') {
    // Don't attempt reconnect if the service is being intentionally stopped
    if (this.isStopping) return;

    console.log(`[Notifications] Handling channel error for ${channelType} channel`);
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[Notifications] Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    
    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    // Exponential backoff for reconnect
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    
    this.reconnectTimeout = setTimeout(async () => {
      console.log(`[Notifications] Attempting to reconnect (attempt ${this.reconnectAttempts})`);
      if (this.userId) {
        await this.setupSubscriptions(this.userId);
      }
    }, delay);
  }

  private handleDisconnect() {
    console.log('[Notifications] Handling disconnect');
    if (this.userId) {
      this.handleChannelError('both');
    }
  }

  public async initialize(userId: string) {
    if (this.isInitialized && this.userId === userId) {
      console.log('[Notifications] Service already initialized for this user');
      return;
    }

    console.log('[Notifications] Initializing service for user:', userId);
    
    // Stop existing service if running for different user
    if (this.isInitialized) {
      await this.stopService();
    }

    this.userId = userId;
    this.isInitialized = true;
    this.reconnectAttempts = 0;
    this.lastPollTime = 0; // Reset last poll time

    try {
      // First check for any existing pending messages
      await this.pollForMessages(userId);

      // Setup real-time subscriptions (as fast path)
      await this.setupSubscriptions(userId);

      // Set up regular polling (as reliable fallback)
      this.setupPolling(userId);

      console.log('[Notifications] Service initialized successfully');
    } catch (error) {
      console.error('[Notifications] Error initializing service:', error);
      await this.stopService();
      throw error;
    }

    return () => {
      this.stopService();
    };
  }

  private setupPolling(userId: string) {
    // Clear any existing polling interval
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    // Poll periodically as a reliable fallback (realtime filter for messages is broad,
    // so polling catches anything that slips through)
    this.pollingInterval = setInterval(() => {
      this.pollForMessages(userId);
    }, this.POLL_INTERVAL);

    // Do an immediate poll
    this.pollForMessages(userId);
  }

  private async pollForMessages(userId: string) {
    // Prevent too frequent polling
    const now = Date.now();
    if (now - this.lastPollTime < this.MIN_POLL_INTERVAL) {
      console.log('[Notifications] Skipping poll - too soon since last poll');
      return;
    }

    try {
      console.log('[Notifications] Polling for messages...');
      this.lastPollTime = now;

      // Get all conversations where the user is a participant
      const { data: conversations, error: convError } = await supabase
        .from('conversations')
        .select('id')
        .or(`initiator_id.eq.${userId},recipient_id.eq.${userId}`);

      if (convError) {
        console.error('[Notifications] Error fetching conversations:', convError);
        return;
      }

      if (!conversations?.length) {
        console.log('[Notifications] No conversations found for user');
        return;
      }

      const conversationIds = conversations.map(c => c.id);

      // Get recent messages that might have been missed
      const fiveMinutesAgo = new Date(now - 5 * 60 * 1000).toISOString();
      
      const { data: recentMessages, error: msgError } = await supabase
        .from('messages')
        .select(`
          id,
          content,
          sender_id,
          conversation_id,
          created_at,
          notification_status,
          conversations!inner (
            initiator_id,
            recipient_id
          ),
          sender:profiles!sender_id (
            contact_name,
            email
          )
        `)
        .in('conversation_id', conversationIds)
        .neq('sender_id', userId) // Don't notify about user's own messages
        .gte('created_at', fiveMinutesAgo) // Only check recent messages
        .or('notification_status.eq.pending,notification_status.is.null')
        .order('created_at', { ascending: true })
        .returns<MessageWithRelations[]>();

      if (msgError) {
        console.error('[Notifications] Error fetching recent messages:', msgError);
        return;
      }

      if (!recentMessages?.length) {
        console.log('[Notifications] No new messages found');
        return;
      }

      console.log('[Notifications] Found messages to process:', recentMessages.length);

      // Process each message
      for (const message of recentMessages) {
        if (!message.conversations || !message.sender) {
          console.warn('[Notifications] Skipping message with missing relations:', message.id);
          continue;
        }
        
        await this.handleNewMessage({
          ...message,
          conversations: {
            initiator_id: message.conversations.initiator_id,
            recipient_id: message.conversations.recipient_id
          },
          sender: {
            contact_name: message.sender.contact_name,
            email: message.sender.email
          }
        });
      }

      // Also check for pending notifications
      const { data: pendingNotifications, error: notifError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'pending')
        .gte('created_at', fiveMinutesAgo);

      if (notifError) {
        console.error('[Notifications] Error fetching notifications:', notifError);
        return;
      }

      if (pendingNotifications?.length) {
        console.log('[Notifications] Found notifications to process:', pendingNotifications.length);
        for (const notification of pendingNotifications) {
          await this.processScheduledNotification(notification);
        }
      }

    } catch (error) {
      console.error('[Notifications] Error during message polling:', error);
    }
  }

  public async stopService() {
    console.log('[Notifications] Stopping notification service');
    
    // Prevent reconnect attempts during teardown
    this.isStopping = true;

    // Clear reconnection timeout if exists
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Clear polling interval
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }

    // Unsubscribe from channels
    await this.cleanupSubscriptions();
    
    // Clear any reconnect timeout that was set during cleanupSubscriptions
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Clear all scheduled notifications
    this.scheduledNotifications.forEach(timeout => clearTimeout(timeout));
    this.scheduledNotifications.clear();
    
    this.isInitialized = false;
    this.userId = null;
    this.reconnectAttempts = 0;
    this.lastPollTime = 0;
    this.isStopping = false;
    
    console.log('[Notifications] Service stopped');
  }

  private async handleNewMessage(message: Message) {
    try {
      const senderName = message.sender?.contact_name || message.sender?.email || 'Someone';

      // Show notification
      const notificationShown = await unifiedNotificationService.showNotification({
        title: `Message from ${senderName}`,
        body: message.content,
        type: 'chat',
        deepLink: `/chat/${message.sender_id}`,
        metadata: {
          messageId: message.id,
          conversationId: message.conversation_id,
          senderId: message.sender_id,
          senderName
        }
      });

      if (notificationShown) {
        // Update message status
        await supabase
          .from('messages')
          .update({
            notification_status: 'delivered',
            notification_delivered_at: new Date().toISOString()
          })
          .eq('id', message.id);

        console.log('[Notifications] Message notification delivered:', {
          messageId: message.id,
          sender: senderName
        });
      } else {
        console.error('[Notifications] Failed to show notification for message:', {
          messageId: message.id,
          sender: senderName
        });
        await supabase
          .from('messages')
          .update({
            notification_status: 'failed'
          })
          .eq('id', message.id);
      }

    } catch (error) {
      console.error('[Notifications] Error handling message:', error);
    }
  }

  private async processScheduledNotification(notification: DatabaseNotification) {
    try {
      // Show the notification first, then mark as delivered
      const shown = await unifiedNotificationService.showNotification({
        title: this.getNotificationTitle(notification),
        body: this.getNotificationBody(notification),
        type: notification.entity_type,
        metadata: notification.metadata,
        deepLink: notification.deep_link
      });

      // Only mark as delivered if the notification was actually shown
      const { error: updateError } = await supabase
        .from('notifications')
        .update({ 
          status: shown ? 'delivered' : 'failed',
          delivered_at: shown ? [new Date().toISOString()] : undefined
        })
        .eq('id', notification.id);

      if (updateError) {
        console.error('[Notifications] Error updating notification status:', updateError);
        return;
      }

      if (shown) {
        this.emit('notificationDelivered', notification);
      }
    } catch (error) {
      console.error('[Notifications] Error processing notification:', error);
      await supabase
        .from('notifications')
        .update({ 
          status: 'failed',
          error_log: { error: error instanceof Error ? error.message : 'Unknown error' }
        })
        .eq('id', notification.id);
    }
  }

  private getBookingNotificationContent(metadata: Record<string, any>, isTitle: boolean = false): string {
    const { service_name, formatted_time, queue_number } = metadata;
    
    if (isTitle) {
      return 'Booking Confirmed ✓';
    }
    
    return `Your appointment for ${service_name} has been successfully booked for ${formatted_time}. Queue number: ${queue_number}`;
  }

  private getBookingReminderContent(metadata: Record<string, any>, isTitle: boolean = false): string {
    const { service_name, formatted_time, queue_number } = metadata;
    
    if (isTitle) {
      return '1h Appointment Reminder';
    }
    
    return `Reminder: Your appointment for ${service_name} is in 1 hour. Queue number: ${queue_number}`;
  }

  public async createNotification(
    userId: string,
    template: NotificationTemplate,
    entityType: string,
    entityId: string,
    scheduledFor: Date = new Date(),
    expiresAt?: Date,
    deepLink?: string
  ): Promise<string | null> {
    try {
      // Skip storing chat notifications in the notifications table
      if (template.category === 'chat') {
        return null;
      }

      // Get notification content based on type
      let title: string;
      let message: string;
      
      if (entityType === 'booking') {
        title = this.getBookingNotificationContent(template.metadata || {}, true);
        message = this.getBookingNotificationContent(template.metadata || {});
      } else if (entityType === 'booking_reminder') {
        title = this.getBookingReminderContent(template.metadata || {}, true);
        message = this.getBookingReminderContent(template.metadata || {});
      } else {
        title = template.name;
        message = 'New notification';
      }

      // Insert notification directly without checking preferences
      const { data, error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          entity_type: entityType,
          entity_id: entityId,
          title,
          message,
          category: template.category || 'system',
          channels: ['in_app', 'push'],
          scheduled_for: scheduledFor.toISOString(),
          expires_at: expiresAt?.toISOString(),
          deep_link: deepLink,
          metadata: template.metadata || {},
          status: 'pending'
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating notification:', error);
        throw error;
      }

      return data?.id || null;
    } catch (error) {
      console.error('Error creating notification:', error);
      return null;
    }
  }

  public async createBookingNotification(
    userId: string,
    bookingId: string,
    serviceName: string,
    scheduledTime: Date,
    queueNumber: string,
    timeFormat: 'gregorian' | 'ethiopian' = 'ethiopian'
  ): Promise<boolean> {
    try {
      console.log('[Notifications] Creating booking notification with params:', {
        userId,
        bookingId,
        serviceName,
        scheduledTime,
        queueNumber,
        timeFormat
      });

      // Format the date and time based on the selected format
      const formattedDateTime = timeFormat === 'ethiopian'
        ? `${getEthiopianDateStr(scheduledTime)} ${getTimeStr(
            scheduledTime.getHours(),
            scheduledTime.getMinutes(),
            false,
            'ethiopian'
          )}`
        : scheduledTime.toLocaleString();

      // Create notification in database first with 'delivered' status
      const notificationId = await this.createNotification(
        userId,
        {
          name: 'booking_confirmation',
          metadata: {
            booking_id: bookingId,
            service_name: serviceName,
            scheduled_time: scheduledTime.toISOString(),
            queue_number: queueNumber,
            formatted_time: formattedDateTime
          }
        },
        'booking',
        bookingId,
        scheduledTime,
        undefined,
        `/bookings/${bookingId}`
      );

      if (!notificationId) {
        console.warn('[Notifications] Failed to store booking notification in database');
        return false;
      }

      // Show notification immediately
      const notificationShown = await unifiedNotificationService.showNotification({
        title: 'Booking Confirmed ✓',
        body: `Your appointment for ${serviceName} has been successfully booked for ${formattedDateTime}. Queue number: ${queueNumber}`,
        type: 'booking',
        deepLink: `/bookings/${bookingId}`,
        metadata: {
          booking_id: bookingId,
          service_name: serviceName,
          scheduled_time: scheduledTime.toISOString(),
          queue_number: queueNumber,
          formatted_time: formattedDateTime
        }
      });

      if (!notificationShown) {
        console.error('[Notifications] Failed to show booking notification');
        return false;
      }

      // Mark the notification as delivered immediately to prevent duplicate
      await supabase
        .from('notifications')
        .update({ 
          status: 'delivered',
          delivered_at: [new Date().toISOString()]
        })
        .eq('id', notificationId);

      return true;
    } catch (error) {
      console.error('[Notifications] Error creating booking notification:', error);
      return false;
    }
  }

  private getNotificationTitle(notification: DatabaseNotification): string {
    switch (notification.entity_type) {
      case 'booking':
        return 'Booking Notification';
      case 'booking_reminder':
        return 'Appointment Reminder';
      default:
        return notification.title;
    }
  }

  private getNotificationBody(notification: DatabaseNotification): string {
    const metadata = notification.metadata || {};
    
    switch (notification.entity_type) {
      case 'booking':
      case 'booking_reminder':
        if (metadata.service_name && metadata.formatted_time) {
          const prefix = notification.entity_type === 'booking_reminder' ? 'Reminder: ' : '';
          if (metadata.queue_number) {
            return `${prefix}Your appointment for ${metadata.service_name} is scheduled for ${metadata.formatted_time}. Queue number: ${metadata.queue_number}`;
          }
          return `${prefix}Your appointment for ${metadata.service_name} is scheduled for ${metadata.formatted_time}`;
        }
        return 'You have an upcoming appointment';
      default:
        return notification.message;
    }
  }
}

// Export a singleton instance
export const notificationService = NotificationService.getInstance();